import { useEffect, useState } from 'react';
import { Modal, TextInput, TextArea } from '@carbon/react';

export interface OntologyDetailsDialogProps {
  isOpen: boolean;
  initialName: string;
  initialDescription?: string;
  /** Called with trimmed name + description when the user saves. */
  onSave: (details: { name: string; description: string }) => void | Promise<void>;
  onClose: () => void;
  /** True while the save request is in flight (disables the primary button). */
  isSaving?: boolean;
}

const NAME_MAX = 200;
const DESCRIPTION_MAX = 2000;

/**
 * OntologyDetailsDialog — rename an ontology and edit its description.
 *
 * The ontology name/description are stored on the ontology row and updated via
 * PATCH /api/ontologies/:id (see ontologyApi.updateOntology). This dialog is the
 * explicit "Edit details" affordance referenced by the editor toolbar: Save
 * persists the graph, Publish snapshots a version, and renaming/describing is a
 * separate, deliberate gesture surfaced here.
 *
 * Validation mirrors the backend: name is required (non-empty after trim) and
 * capped; description is optional and capped.
 */
export const OntologyDetailsDialog = ({
  isOpen,
  initialName,
  initialDescription = '',
  onSave,
  onClose,
  isSaving = false,
}: OntologyDetailsDialogProps) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);

  // Reset the fields to the current ontology values every time the dialog
  // opens, so a cancelled edit never leaks into the next open.
  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription);
    }
  }, [isOpen, initialName, initialDescription]);

  const trimmedName = name.trim();
  const nameEmpty = trimmedName.length === 0;
  const nameTooLong = trimmedName.length > NAME_MAX;
  const descriptionTooLong = description.length > DESCRIPTION_MAX;
  const invalid = nameEmpty || nameTooLong || descriptionTooLong;

  const handleSubmit = () => {
    if (invalid) return;
    void onSave({ name: trimmedName, description: description.trim() });
  };

  return (
    <Modal
      open={isOpen}
      modalHeading="Ontology details"
      primaryButtonText={isSaving ? 'Saving…' : 'Save'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={invalid || isSaving}
      onRequestClose={onClose}
      onRequestSubmit={handleSubmit}
    >
      <TextInput
        id="ontology-details-name"
        data-testid="ontology-details-name"
        labelText="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        invalid={nameEmpty || nameTooLong}
        invalidText={
          nameEmpty
            ? 'Name must not be empty'
            : `Name must be at most ${NAME_MAX} characters`
        }
        maxLength={NAME_MAX + 1}
      />
      <div style={{ marginTop: '1rem' }}>
        <TextArea
          id="ontology-details-description"
          data-testid="ontology-details-description"
          labelText="Description"
          helperText="Optional. e.g. built from papers 1, 3 and 5 in knowledge base XYZ."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          invalid={descriptionTooLong}
          invalidText={`Description must be at most ${DESCRIPTION_MAX} characters`}
          rows={4}
        />
      </div>
    </Modal>
  );
};
